import { Discount, TBeamCartLocalStorage } from "../shared/cart-contents";
import {
  TPromoCodeAttribute,
  PromoCodeSource,
  TPromoCodeRequest,
  TPromoCodeResponse,
  TValidatedPromoResponse,
  TUnvalidatedPromoRequest,
  PROMO_LOCAL_STORAGE_KEY,
  PROMO_CODES_COOKIE_NAME,
  TUnvalidatedPromoCode,
} from "../shared/promo-types";
import { LocalStorageError } from "./beam-errors";
import { getCookieValue, setCookieValue } from "./cookies";
import { createScopedLocalStorage } from "./local-storage";
import { logger } from "./logger";

const URL_QUERY_PARAM_CHAR_LIMIT = 100;
const ALLOWED_URL_PREFIX = "utm_";
// Standard name for Shopify's discount tracking cookie
const DISCOUNT_CODE_COOKIE_NAME = "discount_code";
// < --------- Cookie ------- >

/**
 * Retrieves all validated promo codes stored in the "beam_promo_codes" cookie and parses it
 *
 * @returns {TPromoCodeResponse | undefined} An array of `ValidatedPromoCode` objects if found and parsed successfully, otherwise `undefined`
 */
export const getAllPromoCodesFromCookie = (): TPromoCodeResponse | undefined => {
  const promoCodesString = getCookieValue(PROMO_CODES_COOKIE_NAME);

  if (!promoCodesString) return undefined;

  try {
    const promoCodes: TPromoCodeResponse = JSON.parse(promoCodesString);
    return promoCodes;
  } catch (error) {
    logger.error("Failed to parse beam_promo_codes cookie:", error);
    return undefined;
  }
};

/**
 * Stores the validated promo codes in the "beam_promo_codes" cookie, replaces and does not merge
 *
 * @param {TValidatedPromoResponse} validatedPromoCodes
 * @param {string} domain
 */
export const setPromoCodeInCookie = ({
  validatedPromoCodes,
  domain,
}: {
  validatedPromoCodes: TValidatedPromoResponse;
  domain?: string;
}) => {
  const promoCodeData = {
    validatedPromoCodes: validatedPromoCodes,
  };
  const threeMonthsFromNow = Date.now() + 1000 * 60 * 60 * 24 * 90;

  setCookieValue({
    name: PROMO_CODES_COOKIE_NAME,
    value: JSON.stringify(promoCodeData),
    path: "/",
    domain,
    // If we don't set an expiration, the cookie will be cleared when the tab is closed
    expires: threeMonthsFromNow,
  });
};

// < --------- Local Storage ------- >
/**
 * Retrieves promo codes from localStorage, scoped by API key
 * @param {string} apiKey API key used for storage scoping
 * @returns {TPromoCodeRequest | null} - Returns stored promo codes object containing both
 * validated and unvalidated codes
 * - If no codes exist, returns a fresh object with empty arrays
 *
 */
export const getPromoCodesLocalStorage = ({ apiKey }: { apiKey: string }): TPromoCodeRequest | null => {
  if (!apiKey) {
    logger.error(new LocalStorageError("API key is required to get promo codes"));
    return null;
  }

  const storage = createScopedLocalStorage({ apiKey });
  const allPromos = storage.getItemJson<TPromoCodeRequest>(PROMO_LOCAL_STORAGE_KEY) ?? {
    unvalidatedPromoCodes: [],
    validatedPromoCodes: [],
  };

  return allPromos;
};

/**
 * Sets promo codes in localStorage
 * @param {TPromoCodeRequest} promoCodes New promo codes to add
 * @param {string} apiKey API key for storage scoping
 */
export const setPromoCodesInLocalStorage = ({
  apiKey,
  promoCodes,
}: {
  apiKey?: string;
  promoCodes: Partial<TPromoCodeRequest>;
}): void => {
  if (!apiKey) {
    logger.error(new LocalStorageError("API key is required to set promo codes"));
    return;
  }
  const storage = createScopedLocalStorage({ apiKey });
  const completePromoCodes: TPromoCodeRequest = {
    unvalidatedPromoCodes: promoCodes?.unvalidatedPromoCodes || [],
    validatedPromoCodes: promoCodes?.validatedPromoCodes || [],
  };
  storage.setItemJson(PROMO_LOCAL_STORAGE_KEY, completePromoCodes);
};

/**
 * Merges new promo codes with existing ones and returns the combined result
 * @param {Partial<TPromoCodeRequest>} newPromos New promo codes to add
 * @param {string} apiKey API key for storage scoping
 * @returns {TPromoCodeRequest} An object containing both validated and unvalidated promo codes
 * @example
 * const result = mergePromoCodes({
 *   apiKey: 'beam_123',
 *   newPromos: {
 *     unvalidatedPromoCodes: [
 *       { type: 'manual', attributes: { value: 'WINTER20' } },
 *       { type: 'url', attributes: { url: 'www.example.com/?promo=HOLIDAY25' } }
 *     ]
 *   }
 * });
 */
export const mergeStoreAndReturnPromos = ({
  newPromos,
  apiKey,
}: {
  newPromos: Partial<TPromoCodeRequest>;
  apiKey: string;
}): TPromoCodeRequest => {
  const existing = getPromoCodesLocalStorage({ apiKey }) ?? {
    unvalidatedPromoCodes: [],
    validatedPromoCodes: [],
  };

  const uniqueNewUnvalidatedPromos = (newPromos?.unvalidatedPromoCodes || []).filter((newPromo) => {
    const isDuplicate = existing.unvalidatedPromoCodes.some((existingPromo) => {
      return JSON.stringify(existingPromo) === JSON.stringify(newPromo);
    });
    return !isDuplicate;
  });

  const mergedCodes: TPromoCodeRequest = {
    unvalidatedPromoCodes: [...existing.unvalidatedPromoCodes, ...uniqueNewUnvalidatedPromos],
    validatedPromoCodes: [...existing.validatedPromoCodes, ...(newPromos?.validatedPromoCodes || [])],
  };

  const shouldStore = mergedCodes.unvalidatedPromoCodes.length > 0 || mergedCodes.validatedPromoCodes.length > 0;

  if (shouldStore) {
    setPromoCodesInLocalStorage({ apiKey, promoCodes: mergedCodes });
  }
  return {
    ...mergedCodes,
  };
};

/**
 * Formats unvalidated promo codes by determining their source and attributes
 * The function will assign `QueryParam` as the source if a `url` exists,
 * otherwise, it will assign `Manual` as the source
 *
 * @param {TPromoCodeAttribute[]} promoAttributes An array of promo attributes to be formatted
 * @returns {TUnvalidatedPromoRequest} - An array of UnvalidatedPromoCode objects,
 *    each with a source and attributes object
 */
export const mapToUnvalidatedPromoCodes = (promoAttributes: TPromoCodeAttribute[]): TUnvalidatedPromoRequest => {
  return promoAttributes.reduce<TUnvalidatedPromoRequest>((acc, attr) => {
    const result: TUnvalidatedPromoRequest = acc || [];
    if (attr.url) {
      result.push({
        type: PromoCodeSource.Url,
        attributes: { url: attr.url },
      });
    } else if (attr.value) {
      result.push({
        type: PromoCodeSource.Manual,
        attributes: { value: attr.value },
      });
    } else {
      logger.error("Invalid PromoAttribute - must contain either 'url' or 'value'", { attr });
    }
    return result;
  }, []);
};

/**
 * @param {TPromoCodeAttribute[]} promoAttributes An array of promo attributes to be formatted
 * @param {string} apiKey API key for storage scoping
 * @returns {TPromoCodeRequest} An object containing both validated and unvalidated promo codes
 */
export const formatStoreAndReturnPromoCodes = (
  promoAttributes: TPromoCodeAttribute[],
  apiKey: string,
): TPromoCodeRequest => {
  const unvalidatedPromoCodes = mapToUnvalidatedPromoCodes(promoAttributes);
  return mergeStoreAndReturnPromos({ newPromos: { unvalidatedPromoCodes }, apiKey });
};

/**
 * Extracts applicable promo codes from the cart object
 * This function retrieves discounts stored in the cart's `content.discounts` array,
 * filters out only the applicable promo codes, and returns them as an array of strings
 *
 * @param {TBeamCartLocalStorage} cart The cart object retrieved from local storage
 * @returns {string[]} An array of applicable promo codes
 */
export const getPromoCodesFromCart = (cart: TBeamCartLocalStorage): string[] => {
  if (!cart?.content) return [];

  const cartLevelCodes = cart.content.discounts?.filter((d) => d.applicable)?.map((d) => d.code) || [];

  const itemLevelCodes =
    cart.content.items?.flatMap(
      (item) => item.discounts?.filter((d: Discount) => d.applicable)?.map((d: Discount) => d.code) || [],
    ) || [];

  const allCodes = [...cartLevelCodes, ...itemLevelCodes];

  return [...new Set(allCodes)];
};

/**
 * Gets promo code exclusively from Shopify discount cookie
 * @returns {string | null} Single discount code or null if not found
 */
export const getPromoCodeFromShopifyCookie = (): string | null => {
  return getCookieValue(DISCOUNT_CODE_COOKIE_NAME) ?? null;
};

/**
 * Safely parses a JSON string into an array of strings
 * If parsing fails or the input is not a valid JSON array, it returns an empty array
 *
 * @param {string | undefined | null} jsonString The JSON string to parse
 * @returns {string[]} The parsed array of strings or an empty array if parsing fails
 */
export const parseJsonStringArray = (jsonString: string | undefined | null): string[] => {
  if (!jsonString) return [];
  try {
    const parsed = JSON.parse(jsonString);
    if (Array.isArray(parsed) && parsed.every((item) => typeof item === "string")) {
      return parsed;
    }
    return [];
  } catch (error) {
    logger.error("Failed to parse JSON:", error);
    return [];
  }
};

/**
 * Retrieves and sanitizes the current URL for storage, preserving only safe query parameters
 *
 * @returns {TPromoCodeAttribute} Returns an object with only the `url` property set
 */
export const getSanitizedUrlForStorage = (): TPromoCodeAttribute | undefined => {
  try {
    const url = new URL(window.location.href);

    if (!url.search) return undefined;

    // Create NEW URL instead of modifying existing one
    const cleanUrl = new URL(url.origin + url.pathname);
    let hasValidParams = false;

    // Process all parameters
    url.searchParams.forEach((value, key) => {
      const isAllowed = key.startsWith(ALLOWED_URL_PREFIX);
      const isValidLength = value.length <= URL_QUERY_PARAM_CHAR_LIMIT;

      if (isAllowed && isValidLength) {
        cleanUrl.searchParams.append(key, value);
        hasValidParams = true;
      }
    });

    const finalUrl = cleanUrl.toString();
    return hasValidParams ? { url: finalUrl } : undefined;
  } catch (error) {
    return undefined;
  }
};

export const promoCodesAreEqual = (a: TPromoCodeRequest, b: TPromoCodeRequest): boolean => {
  const extract = (codes: unknown): string => {
    const validCodes = (Array.isArray(codes) ? codes : []) as TUnvalidatedPromoCode[];

    const flattened = validCodes
      .map((c) => {
        if (c.type === "manual") {
          return c.attributes?.value;
        }
        if (c.type === "url") {
          return c.attributes?.url;
        }
        return "";
      })
      .filter(Boolean)
      .sort()
      .join(",");
    return flattened;
  };

  return extract(a?.unvalidatedPromoCodes) === extract(b?.unvalidatedPromoCodes);
};

export const isEmptyPromoData = (promos: {
  unvalidatedPromoCodes?: unknown[];
  validatedPromoCodes?: unknown[];
}): boolean => {
  return (
    (!promos.unvalidatedPromoCodes || promos.unvalidatedPromoCodes.length === 0) &&
    (!promos.validatedPromoCodes || promos.validatedPromoCodes.length === 0)
  );
};
